/**
 * headshotResolution — the pure "which document is this person's headshot"
 * logic shared by every display surface that wants a contestant's official
 * pageant headshot to take priority over their separately-uploaded, evergreen
 * profile picture (`Person.profilePhotoS3Key`).
 *
 * A headshot is a `DocumentReference` (`documentType === 'HEADSHOT'`), one per
 * competition year — unlike the profile picture, a contestant can have a
 * different headshot each season. "Most recent" is the only ordering signal
 * available across years, so callers with a multi-year document list (e.g. a
 * Community record) get the latest one; callers already scoped to a single
 * year (e.g. reviewing that year's application) effectively pick from just
 * that year's rows.
 *
 * Pure and dependency-free by design — turning a picked document into a
 * viewable image still requires a presign call (`getDocumentDownloadUrl`),
 * which is intentionally NOT done here.
 */

/** The minimal document shape this module needs — matches both `ReviewDocument` and Community's `DocumentRow`. */
export interface HeadshotCandidate {
  id: string;
  documentType: string;
  uploadedAt?: string;
}

/**
 * Pick the most recently uploaded HEADSHOT document from a list, or `null` if
 * none exists. Ties / missing `uploadedAt` fall back to input order (the
 * first HEADSHOT encountered), since there is no other signal to break a tie.
 */
export function pickMostRecentHeadshot(
  documents: readonly HeadshotCandidate[],
): HeadshotCandidate | null {
  let best: HeadshotCandidate | null = null;
  for (const doc of documents) {
    if (doc.documentType !== 'HEADSHOT') continue;
    if (!best) {
      best = doc;
      continue;
    }
    if ((doc.uploadedAt ?? '') > (best.uploadedAt ?? '')) {
      best = doc;
    }
  }
  return best;
}
